import { useEffect, useId, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * Small dropdowns built on <details>: they open without script and keep
 * button semantics. `useMenus()` (once per page) adds what a menu needs:
 * it opens where it fits (never past the window's or a dialog's edge),
 * Escape or a click elsewhere closes it, arrows move through its items,
 * choosing an item closes it.
 */
const OPEN = 'details.ui-menu[open], details.menu[open]';
const ITEMS = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

/** A menu's panel: its own child, or the one drawn in <body> for it (PortalPanel). */
function panelOf(menu: HTMLDetailsElement): HTMLElement | null {
  const id = menu.dataset.portal;
  if (id) return document.querySelector<HTMLElement>(`[data-portal-panel="${CSS.escape(id)}"]`);
  return menu.querySelector<HTMLElement>(':scope > :not(summary)');
}

/** The menu an element belongs to, through a panel drawn in <body> too. */
function menuOf(el: Element | null): HTMLDetailsElement | null {
  const own = el?.closest<HTMLDetailsElement>('details.ui-menu, details.menu');
  if (own) return own;
  const id = el?.closest<HTMLElement>('[data-portal-panel]')?.dataset.portalPanel;
  return id ? document.querySelector<HTMLDetailsElement>(`details[data-portal="${CSS.escape(id)}"]`) : null;
}

const inMenu = (menu: HTMLDetailsElement, node: Node | null): boolean => node !== null && (menu.contains(node) || (panelOf(menu)?.contains(node) ?? false));

function panelItems(menu: HTMLDetailsElement): HTMLElement[] {
  const panel = panelOf(menu);
  return panel ? [...panel.querySelectorAll<HTMLElement>(ITEMS)].filter((el) => el.getClientRects().length > 0) : [];
}

const GAP = 6;
const EDGE = 8;

/** What a menu's panel may fill: the window, cut by every ancestor that clips its content (a dialog, a scroll area). */
function room(menu: HTMLElement): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  for (let el = menu.parentElement; el && el !== document.documentElement; el = el.parentElement) {
    if (getComputedStyle(el).overflowY === 'visible') continue;
    const box = el.getBoundingClientRect();
    top = Math.max(top, box.top);
    bottom = Math.min(bottom, box.bottom);
  }
  return { top, bottom };
}

/**
 * A menu opens where it fits: in its own direction, the other way when that has more room, and never past the edge
 * of the window or of a dialog around it; a panel taller than the room it has scrolls.
 */
function place(menu: HTMLDetailsElement): void {
  const panel = menu.querySelector<HTMLElement>(':scope > .ui-popover');
  const trigger = menu.querySelector<HTMLElement>(':scope > summary');
  if (!panel || !trigger) return;
  const wanted = (panel.dataset.direction ??= panel.classList.contains('ui-popover--up') ? 'up' : 'down');
  panel.style.maxHeight = '';
  panel.style.overflowY = '';
  const height = panel.getBoundingClientRect().height;
  const { top, bottom } = room(menu);
  const at = trigger.getBoundingClientRect();
  const below = bottom - at.bottom - GAP - EDGE;
  const above = at.top - top - GAP - EDGE;
  const down = wanted === 'down' ? height <= below || below >= above : height > above && below > above;
  panel.classList.toggle('ui-popover--down', down);
  panel.classList.toggle('ui-popover--up', !down);
  const space = Math.floor(down ? below : above);
  if (height > space) {
    panel.style.maxHeight = `${Math.max(space, 96)}px`;
    panel.style.overflowY = 'auto';
  }
}

export function useMenus(): void {
  useEffect(() => {
    // an open menu whose content changes size (its places still loading when it opened) is placed again
    const watched = new Map<HTMLDetailsElement, ResizeObserver>();
    const watch = (menu: HTMLDetailsElement) => {
      const panel = menu.querySelector<HTMLElement>(':scope > .ui-popover');
      if (!panel || watched.has(menu) || typeof ResizeObserver === 'undefined') return;
      let natural = panel.scrollHeight;
      const observer = new ResizeObserver(() => {
        if (!menu.open || panel.scrollHeight === natural) return;
        natural = panel.scrollHeight;
        place(menu);
      });
      for (const child of panel.children) observer.observe(child);
      observer.observe(panel);
      watched.set(menu, observer);
    };
    const unwatch = (menu: HTMLDetailsElement) => {
      watched.get(menu)?.disconnect();
      watched.delete(menu);
    };
    const closeAll = (keep: EventTarget | null) => {
      for (const menu of document.querySelectorAll<HTMLDetailsElement>(OPEN)) {
        if (!(keep instanceof Node && inMenu(menu, keep))) menu.open = false;
      }
    };
    const onPointerDown = (event: PointerEvent) => closeAll(event.target);
    const onKeyDown = (event: KeyboardEvent) => {
      const menus = [...document.querySelectorAll<HTMLDetailsElement>(OPEN)];
      const menu = menus.find((m) => inMenu(m, document.activeElement)) ?? menus.at(-1);
      if (!menu) return;
      if (event.key === 'Escape') {
        menu.open = false;
        menu.querySelector<HTMLElement>(':scope > summary')?.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      // inside a text field the arrows move the caret
      const active = document.activeElement;
      if (active instanceof HTMLTextAreaElement || (active instanceof HTMLInputElement && active.type !== 'checkbox' && active.type !== 'radio')) return;
      const items = panelItems(menu);
      if (items.length === 0) return;
      event.preventDefault();
      const index = items.indexOf(active as HTMLElement);
      const next =
        event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
      items[next]?.focus();
    };
    const onClick = (event: MouseEvent) => {
      const item = event.target instanceof Element ? event.target.closest('.ui-menu-item') : null;
      const menu = item ? menuOf(item) : null;
      if (item && menu && !item.hasAttribute('data-keep-open')) menu.open = false;
    };
    // an opened menu is placed where it fits; one opened with the keyboard puts focus on its first item
    const onToggle = (event: Event) => {
      const menu = event.target;
      if (!(menu instanceof HTMLDetailsElement) || !menu.matches('.ui-menu, .menu')) return;
      if (!menu.open) {
        unwatch(menu);
        return;
      }
      // a panel drawn in <body> places itself and puts the focus on its first item once it is there
      if (menu.dataset.portal) return;
      place(menu);
      watch(menu);
      if (menu.contains(document.activeElement) && document.activeElement?.tagName === 'SUMMARY' && menu.dataset.keyboard === '1') panelItems(menu)[0]?.focus();
      delete menu.dataset.keyboard;
    };
    const onSummaryKey = (event: KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === ' ') && event.target instanceof HTMLElement && event.target.tagName === 'SUMMARY') {
        const menu = event.target.parentElement;
        if (menu instanceof HTMLDetailsElement) menu.dataset.keyboard = '1';
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keydown', onSummaryKey, true);
    document.addEventListener('click', onClick);
    document.addEventListener('toggle', onToggle, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keydown', onSummaryKey, true);
      document.removeEventListener('click', onClick);
      document.removeEventListener('toggle', onToggle, true);
      for (const observer of watched.values()) observer.disconnect();
      watched.clear();
    };
  }, []);
}

const GAP_FLOAT = 6;

/** A panel in <body> under its trigger (over it when the room below is short), its right edge under the trigger's in the right half. */
function placeFloating(trigger: HTMLElement, panel: HTMLElement, align: 'left' | 'right' | 'auto'): void {
  panel.style.maxHeight = '';
  panel.style.overflowY = '';
  const at = trigger.getBoundingClientRect();
  const { width, height } = panel.getBoundingClientRect();
  const fromRight = align === 'right' || (align === 'auto' && at.left + at.width / 2 > window.innerWidth / 2);
  const left = Math.max(EDGE, Math.min(fromRight ? at.right - width : at.left, window.innerWidth - width - EDGE));
  const below = window.innerHeight - at.bottom - GAP_FLOAT - EDGE;
  const above = at.top - GAP_FLOAT - EDGE;
  const down = height <= below || below >= above;
  const room = Math.floor(down ? below : above);
  const shown = Math.min(height, Math.max(room, 96));
  if (height > room) {
    panel.style.maxHeight = `${Math.max(room, 96)}px`;
    panel.style.overflowY = 'auto';
  }
  panel.style.setProperty('--float-left', `${Math.round(left)}px`);
  panel.style.setProperty('--float-top', `${Math.round(down ? at.bottom + GAP_FLOAT : at.top - GAP_FLOAT - shown)}px`);
}

/**
 * A menu's panel drawn in <body> while the menu is open (v0.15e.2, D-218): a table cell, a scroll area or a
 * container around the trigger cannot cut it off or paint over it. It has the popover's opaque background, sits
 * above the page (inside an open dialog when the trigger is in one), opens under its trigger or over it, and
 * follows the trigger on scroll. useMenus keeps its closing (Escape, a click elsewhere, choosing an item) and
 * its arrow keys.
 */
export function PortalPanel({
  menu,
  align = 'auto',
  className,
  children,
  ...rest
}: { menu: RefObject<HTMLDetailsElement | null>; align?: 'left' | 'right' | 'auto' } & HTMLAttributes<HTMLDivElement>) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const details = menu.current;
    if (!details) return;
    details.dataset.portal = id;
    const sync = () => setHost(details.open ? (details.closest<HTMLElement>('dialog[open]') ?? document.body) : null);
    sync();
    details.addEventListener('toggle', sync);
    return () => {
      details.removeEventListener('toggle', sync);
      delete details.dataset.portal;
    };
  }, [menu, id]);
  useLayoutEffect(() => {
    const details = menu.current;
    const trigger = details?.querySelector<HTMLElement>(':scope > summary');
    const element = panel.current;
    if (!host || !details || !trigger || !element) return;
    const follow = () => placeFloating(trigger, element, align);
    follow();
    // opened with the keyboard: the focus goes to the first item
    if (details.dataset.keyboard === '1') panelItems(details)[0]?.focus();
    delete details.dataset.keyboard;
    let pending = 0;
    const later = () => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(follow);
    };
    // its content can change size after it opened (a value box appears)
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(later);
    observer?.observe(element);
    window.addEventListener('scroll', later, true);
    window.addEventListener('resize', later);
    return () => {
      cancelAnimationFrame(pending);
      observer?.disconnect();
      window.removeEventListener('scroll', later, true);
      window.removeEventListener('resize', later);
    };
  }, [host, menu, align]);
  if (!host) return null;
  return createPortal(
    <div ref={panel} className={['ui-popover', 'ui-popover--portal', className ?? ''].join(' ').trim()} data-portal-panel={id} {...rest}>
      {children}
    </div>,
    host,
  );
}

export interface MenuProps {
  /** the trigger's content (text, an icon, both) */
  trigger: ReactNode;
  /** the trigger's accessible name when it shows only an icon */
  label?: string;
  children: ReactNode;
  align?: 'left' | 'right';
  direction?: 'down' | 'up';
  className?: string;
  triggerClassName?: string;
  panelClassName?: string;
  /** D-218: the panel is drawn in <body> (a menu in a table or a scroll area) */
  portal?: boolean;
  'data-testid'?: string;
}

export function Menu({ trigger, label, children, align = 'left', direction = 'down', className, triggerClassName, panelClassName, portal = false, 'data-testid': testId }: MenuProps) {
  const ref = useRef<HTMLDetailsElement>(null);
  return (
    <details ref={ref} className={['ui-menu', className ?? ''].join(' ').trim()} data-testid={testId}>
      <summary className={triggerClassName} aria-label={label} title={label}>
        {trigger}
      </summary>
      {portal ? (
        <PortalPanel menu={ref} align={align} className={panelClassName} data-testid={testId ? `${testId}-panel` : undefined}>
          {children}
        </PortalPanel>
      ) : (
        <div className={['ui-popover', `ui-popover--${direction}`, align === 'right' ? 'ui-popover--right' : '', panelClassName ?? ''].join(' ').trim()}>{children}</div>
      )}
    </details>
  );
}

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  hint?: ReactNode;
  danger?: boolean;
  /** stays open after the click (a toggle inside the menu) */
  keepOpen?: boolean;
}

export function MenuItem({ icon, hint, danger, keepOpen, className, children, type = 'button', ...rest }: MenuItemProps) {
  return (
    <button
      type={type}
      className={['ui-menu-item', danger ? 'ui-menu-item--danger' : '', className ?? ''].join(' ').trim()}
      data-keep-open={keepOpen ? '' : undefined}
      {...rest}
    >
      {icon}
      <span>{children}</span>
      {hint ? <span className="ui-menu-item__hint">{hint}</span> : null}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="ui-menu-separator" role="separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <p className="ui-menu-label">{children}</p>;
}
